import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { TRIGGER_SUBTYPES, type TriggerSubtype } from "@proclaw/shared";
import { Field, TextInput, Textarea, Select } from "./form-primitives";

const SUBTYPE_LABELS: Record<TriggerSubtype, string> = {
  manual: "Manual — user clicks Run",
  schedule: "Schedule — cron expression",
  webhook: "Webhook — external HTTP call",
  channel_message: "Channel message — new message posted",
};

/**
 * Config form for a Trigger node. Editable only when admin; read-only
 * otherwise (caller passes `readOnly`).
 */
export function TriggerConfig({
  data,
  onChange,
  readOnly,
  workflowId,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly: boolean;
  workflowId?: string;
}) {
  const subtype = (data.subtype as TriggerSubtype) ?? "manual";
  const update = (patch: Record<string, unknown>) => onChange({ ...data, ...patch });
  return (
    <div className="space-y-4">
      <Field label="Label">
        <TextInput
          value={(data.label as string) ?? ""}
          onChange={(v) => update({ label: v })}
          placeholder="e.g. New Lead Received"
          readOnly={readOnly}
        />
      </Field>

      <Field label="Trigger type">
        <Select
          value={subtype}
          onChange={(v) => update({ subtype: v as TriggerSubtype })}
          disabled={readOnly}
          options={TRIGGER_SUBTYPES.map((s) => ({ value: s, label: SUBTYPE_LABELS[s] }))}
        />
      </Field>

      {subtype === "schedule" && (
        <>
          <Field label="Cron expression">
            <TextInput
              value={(data.cronExpression as string) ?? ""}
              onChange={(v) => update({ cronExpression: v })}
              placeholder="0 9 * * *"
              readOnly={readOnly}
              mono
            />
            <p className="mt-1 text-[11px] text-text-muted">
              Standard 5-field cron (minute hour day-of-month month day-of-week).
            </p>
          </Field>
          <Field label="Timezone">
            <TextInput
              value={(data.timezone as string) ?? "UTC"}
              onChange={(v) => update({ timezone: v })}
              placeholder="UTC"
              readOnly={readOnly}
            />
          </Field>
        </>
      )}

      {subtype === "webhook" && (
        <WebhookFields
          workflowId={workflowId}
          webhookId={(data.webhookId as string) ?? ""}
        />
      )}

      <Field label="Description">
        <Textarea
          value={(data.description as string) ?? ""}
          onChange={(v) => update({ description: v })}
          placeholder="What fires this workflow?"
          readOnly={readOnly}
          rows={3}
        />
      </Field>
    </div>
  );
}

/**
 * Surfaces the live incoming-webhook URL + secret for a webhook-triggered
 * workflow. The secret (`wh_…`) is provisioned server-side on first save and
 * embedded in the URL path; the route authenticates on it (constant-time).
 * The URL is same-origin with the platform the user is viewing, so we build
 * it from window.location.origin.
 */
function WebhookFields({
  workflowId,
  webhookId,
}: {
  workflowId?: string;
  webhookId: string;
}) {
  const [copied, setCopied] = useState(false);
  const url =
    webhookId && workflowId
      ? `${window.location.origin}/api/workflows/${workflowId}/webhook/${webhookId}`
      : "";

  const copy = () => {
    if (!url) return;
    void navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!url) {
    return (
      <Field label="Webhook URL">
        <TextInput
          value="(generated when you save the workflow)"
          onChange={() => undefined}
          readOnly
          mono
        />
        <p className="mt-1 text-[11px] text-text-muted">
          Save the workflow once to generate its unique webhook URL and secret.
        </p>
      </Field>
    );
  }

  return (
    <Field label="Webhook URL">
      <div className="flex items-start gap-1.5">
        <div className="min-w-0 flex-1">
          <TextInput value={url} onChange={() => undefined} readOnly mono />
        </div>
        <button
          type="button"
          onClick={copy}
          aria-label="Copy webhook URL"
          title={copied ? "Copied" : "Copy URL"}
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded border border-border text-text-muted transition-colors hover:bg-background hover:text-text-primary"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      </div>
      <p className="mt-1.5 text-[11px] leading-relaxed text-text-muted">
        Send an HTTP <span className="font-mono">POST</span> with a JSON body to this URL to fire the
        workflow; the body is handed to the agent as the trigger input. The secret in the URL
        authenticates the call — treat the whole URL like a password and share it only with trusted
        systems. The webhook fires only while the workflow status is{" "}
        <strong className="text-text-primary">Active</strong>; set it inactive to disable it
        instantly.
      </p>
    </Field>
  );
}
